import React, { useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { baseLineOptions, seriesColour, shortDate } from './charts';
import { pillarColour, NAVY, TEAL, LIGHT_BLUE, MAUVE } from './palette';
import { fmtValue, metricHeading } from './format';

const MAX_FOCUS = 10;
const BINS = 24;

// The funds the summary charts focus on: ticked rows, else the top rows on screen.
function focusRows(rows, selected) {
  const sel = rows.filter((r) => selected.has(r.tr_code));
  return (sel.length ? sel : rows).slice(0, MAX_FOCUS);
}

const legend = { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 }, usePointStyle: true } };

// Time series for the selected funds: each statistic column over time, then the total
// score and each pillar score over time (0-100), all from /analysis/history.
function HistoryTiles({ history, catalogue, error }) {
  if (error) return <div className="qs-tile qs-tile-wide"><div className="qs-muted">History: {error}</div></div>;
  if (!history) return <div className="qs-tile qs-tile-wide"><div className="qs-muted">Loading history for the selected funds…</div></div>;
  const labels = history.dates.map(shortDate);
  const funds = history.funds;
  const lineFor = (f, i, data) => ({ label: history.names[f] || f, data, borderColor: seriesColour(i), backgroundColor: seriesColour(i) });
  const base = baseLineOptions('ratio');
  const scoreOpts = { ...base, scales: { ...base.scales, y: { ...base.scales.y, min: 0, max: 100, ticks: { font: { size: 10 }, color: '#7F7F7F' } } } };
  return (
    <>
      {history.metrics.map((m) => (
        <div key={m.key} className="qs-tile">
          <div className="qs-tile-title">{metricHeading(m, catalogue)} <span className="qs-muted">over time{m.in_weighting ? ' · in the score' : ''}</span></div>
          <div style={{ height: 240 }}>
            <Line data={{ labels, datasets: funds.map((f, i) => lineFor(f, i, m.series[f].value)) }} options={baseLineOptions(m.unit)} />
          </div>
        </div>
      ))}
      {history.scores && (
        <>
          <div className="qs-tile">
            <div className="qs-tile-title">Total score <span className="qs-muted">{history.scores.name} · 0-100 at each month-end</span></div>
            <div style={{ height: 240 }}>
              <Line data={{ labels, datasets: funds.map((f, i) => lineFor(f, i, history.scores.total[f])) }} options={scoreOpts} />
            </div>
          </div>
          {history.scores.pillars.map((p, k) => (
            <div key={p} className="qs-tile">
              <div className="qs-tile-title"><span className="qs-pillar-dot" style={{ background: pillarColour(k) }} /> {p} pillar <span className="qs-muted">0-100 at each month-end</span></div>
              <div style={{ height: 240 }}>
                <Line data={{ labels, datasets: funds.map((f, i) => lineFor(f, i, history.scores.by_pillar[p][f])) }} options={scoreOpts} />
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}

// Total score of the focus funds split into each metric's contribution (stacked bar).
function ScoreDecomposition({ result, rows, selected }) {
  const focus = useMemo(() => focusRows(rows, selected).filter((r) => r.score?.total !== null && r.score?.total !== undefined), [rows, selected]);
  if (!focus.length) return null;
  const metrics = result.weighting.pillars.flatMap((p) => p.metrics.map((m) => ({ ...m, pillar: p.name })));
  const pillarIndex = Object.fromEntries(result.weighting.pillars.map((p, i) => [p.name, i]));
  const shade = (pi, j, n) => {
    const base = pillarColour(pi);
    const alpha = n > 1 ? 1 - (j / n) * 0.55 : 1;
    const r = parseInt(base.slice(1, 3), 16); const g = parseInt(base.slice(3, 5), 16); const b = parseInt(base.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
  };
  const perPillarCount = {};
  const datasets = metrics.map((m) => {
    const pi = pillarIndex[m.pillar];
    const j = perPillarCount[m.pillar] || 0; perPillarCount[m.pillar] = j + 1;
    const n = result.weighting.pillars[pi].metrics.length;
    return {
      label: `${m.pillar}: ${m.label}`,
      data: focus.map((r) => r.score.metrics.find((x) => x.key === m.key)?.contribution ?? 0),
      backgroundColor: shade(pi, j, n), borderWidth: 0, stack: 'score',
    };
  });
  return (
    <div className="qs-tile qs-tile-wide">
      <div className="qs-tile-title">Score decomposition <span className="qs-muted">{selected.size ? 'selected funds' : `top ${focus.length} on screen`} · each metric&apos;s contribution to the total</span></div>
      <div style={{ height: 70 + 30 * focus.length }}>
        <Bar
          data={{ labels: focus.map((r) => r.name || r.tr_code), datasets }}
          options={{ indexAxis: 'y', responsive: true, maintainAspectRatio: false, animation: false,
            scales: { x: { stacked: true, min: 0, max: 100, grid: { color: '#EEF1F4' }, ticks: { font: { size: 10 } } },
              y: { stacked: true, ticks: { font: { size: 11 }, color: '#231F20', autoSkip: false, crossAlign: 'far' }, grid: { display: false }, afterFit: (s) => { s.width = Math.min(360, Math.max(s.width, 220)); } } },
            plugins: { legend, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${c.parsed.x.toFixed(1)}` } } } }}
        />
      </div>
    </div>
  );
}

// Percentile profile: one line per focus fund across the statistic columns (0-100).
function PercentileProfile({ result, rows, catalogue, selected }) {
  const focus = useMemo(() => focusRows(rows, selected), [rows, selected]);
  const metrics = result.metrics;
  if (!focus.length || !metrics.length) return null;
  const labels = metrics.map((m) => metricHeading(m, catalogue));
  const datasets = focus.map((r, i) => ({
    label: r.name || r.tr_code,
    data: metrics.map((m) => r.metrics[m.key]?.percentile ?? null),
    borderColor: seriesColour(i), backgroundColor: seriesColour(i), pointRadius: 4, pointHoverRadius: 6, borderWidth: 1.6, tension: 0,
  }));
  return (
    <div className="qs-tile">
      <div className="qs-tile-title">Percentile profile <span className="qs-muted">{selected.size ? 'selected funds' : `top ${focus.length} on screen`} · percentile within the sub asset class per column</span></div>
      <div style={{ height: 280 }}>
        <Line
          data={{ labels, datasets }}
          options={{ responsive: true, maintainAspectRatio: false, animation: false,
            scales: { x: { ticks: { font: { size: 10 }, color: '#3F4A59' }, grid: { color: '#EEF1F4' } },
              y: { min: 0, max: 100, ticks: { font: { size: 10 }, color: '#7F7F7F', stepSize: 25 }, grid: { color: '#EEF1F4' } } },
            plugins: { legend, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${c.parsed.y === null ? '–' : Math.round(c.parsed.y)}th` } } } }}
        />
      </div>
    </div>
  );
}

// Histogram of the sorted statistic across the cohort (or the rows on screen), with
// the focus funds marked at their bins.
function Histogram({ result, rows, sort, catalogue, distribution, selected }) {
  const sortedKey = sort.key.includes(':') ? sort.key.split(':')[0] : (result.metrics[0]?.key || null);
  const metric = result.metrics.find((m) => m.key === sortedKey);
  const focus = useMemo(() => focusRows(rows, selected), [rows, selected]);
  if (!metric) return null;
  const cohort = distribution && distribution.metric?.key === metric.key ? distribution : null;
  const pts = cohort ? cohort.points.map((p) => ({ tr: p.tr_code, name: p.name, v: p.value }))
    : rows.map((r) => ({ tr: r.tr_code, name: r.name, v: r.metrics[metric.key]?.value })).filter((p) => p.v !== null && p.v !== undefined);
  if (pts.length < 2) return null;
  const vals = pts.map((p) => p.v);
  const lo = Math.min(...vals); const hi = Math.max(...vals);
  const width = (hi - lo) / BINS || 1e-9;
  const counts = new Array(BINS).fill(0);
  vals.forEach((v) => { counts[Math.min(BINS - 1, Math.floor((v - lo) / width))] += 1; });
  const binLabel = (i) => fmtValue(lo + (i + 0.5) * width, metric.unit, metric.unit === 'pct' ? 1 : 2);
  const labels = counts.map((_, i) => binLabel(i));
  const markers = focus.map((r) => {
    const v = r.metrics[metric.key]?.value;
    if (v === null || v === undefined) return null;
    const b = Math.min(BINS - 1, Math.floor((v - lo) / width));
    return { x: labels[b], y: counts[b] + Math.max(1, Math.round(Math.max(...counts) * 0.04)), name: r.name || r.tr_code, v };
  }).filter(Boolean);
  const bench = cohort?.benchmark?.value;
  const benchBin = bench === null || bench === undefined ? null : Math.min(BINS - 1, Math.floor((bench - lo) / width));
  return (
    <div className="qs-tile">
      <div className="qs-tile-title">
        Distribution of {metricHeading(metric, catalogue)}
        <span className="qs-muted"> {cohort ? `${cohort.sub_asset_class}: ${cohort.n_valued} funds` : `${pts.length} rows on screen (pick one sub asset class for the whole cohort)`}{benchBin !== null ? ' · mauve bar = benchmark fund' : ''}</span>
      </div>
      <div style={{ height: 280 }}>
        <Bar
          data={{ labels, datasets: [
            { type: 'bar', label: 'funds', data: counts, backgroundColor: counts.map((_, i) => (i === benchBin ? MAUVE : LIGHT_BLUE)), borderWidth: 0, barPercentage: 1, categoryPercentage: 0.95, order: 2 },
            { type: 'line', label: selected.size ? 'selected' : 'top on screen', data: markers.map((m) => ({ x: m.x, y: m.y, name: m.name, v: m.v })), showLine: false,
              pointRadius: 6, pointStyle: 'triangle', rotation: 180, pointBackgroundColor: NAVY, pointBorderColor: NAVY, order: 1 },
          ] }}
          options={{ responsive: true, maintainAspectRatio: false, animation: false,
            scales: { x: { ticks: { font: { size: 9 }, color: '#7F7F7F', maxRotation: 0, autoSkip: true, maxTicksLimit: 8 }, grid: { display: false } },
              y: { ticks: { font: { size: 10 }, color: '#7F7F7F', precision: 0 }, grid: { color: '#EEF1F4' }, title: { display: true, text: 'funds', font: { size: 10 } } } },
            plugins: { legend, tooltip: { callbacks: { label: (c) => (c.dataset.type === 'line' ? `${c.raw.name}: ${fmtValue(c.raw.v, metric.unit)}` : `${c.parsed.y} funds around ${c.label}`) } } } }}
        />
      </div>
      <div className="qs-muted" style={{ color: TEAL }}>{focus.length ? `${focus.length} ${selected.size ? 'selected' : 'top'} fund${focus.length > 1 ? 's' : ''} marked` : ''}</div>
    </div>
  );
}

export default function ChartPanel({ result, rows, sort, catalogue, selected, distribution, history, historyError }) {
  if (!result || !rows.length) return null;
  return (
    <>
      <div className="qs-charts">
        {selected.size > 0
          ? <HistoryTiles history={history} catalogue={catalogue} error={historyError} />
          : <div className="qs-tile qs-tile-wide"><div className="qs-muted">Tick funds in the grid to chart each statistic, the total score and each pillar over time.</div></div>}
      </div>
      <div className="qs-charts">
        {result.weighting && <ScoreDecomposition result={result} rows={rows} selected={selected} />}
        <PercentileProfile result={result} rows={rows} catalogue={catalogue} selected={selected} />
        <Histogram result={result} rows={rows} sort={sort} catalogue={catalogue} distribution={distribution} selected={selected} />
      </div>
    </>
  );
}
